import { useContext, useEffect } from 'react';
import { Route, Routes, Navigate, Outlet, useLocation } from 'react-router-dom';

import {
  productType,
  routesPaths,
  GTMBodyScript,
  GTMHeaderScript,
  RecaptchaScript,
  routesPathsList,
  ChecklistScript,
} from '@selfserve/react-utils';
import {
  useCurrentRoute,
  useLoadScript,
} from '@selfserve/react-hooks';

import {
  CreateAccountExtended,
  CreateAccountBase,
} from '@selfserve/react-feature-create-account';
import {
  GetStarted,
  Verify,
  LinkUsed,
  OneLastStep,
} from '@selfserve/react-feature-get-started';
import { MarketingInfo } from '@selfserve/react-feature-marketing-info';
import {
  BuildYourPlan,
  Confirmed,
  Checkout,
  PleaseWait,
} from '@selfserve/react-feature-purchase-plan';
import {
  PlansOverview,
  BillingDetails,
  InvoiceHistory,
  AccountLayout,
} from '@selfserve/react-feature-account-management';
import { Login, CheckYourInbox } from '@selfserve/react-feature-login';
import {
  ForgotPassword,
  SetPassword,
} from '@selfserve/react-feature-forgot-password';
import {
  AdminLayout,
  AdminProducts,
  AdminPackageUpdate,
  AdminPlanUpdate,
  AdminPartners,
  AdminEnterprise,
  AdminSubscriptions,
  AdminChecklists,
} from '@selfserve/react-feature-admin';
import { WebinarCreatorWizard } from '@selfserve/react-feature-webinar-creator';
import MarketingInfoContext from 'libs/react-feature-marketing-info/src/lib/utils/markerting-info-context';
import {AdminAnnouncements} from "libs/react-feature-admin/src/lib/feature/admin-announcements";

export function App() {
  const { pathname } = useLocation();
  const currentRoute = useCurrentRoute(routesPathsList);
  const bodyScript = GTMBodyScript();
  const headerScript = GTMHeaderScript();
  const recaptchaScript = RecaptchaScript();
  const checklistScript = ChecklistScript();
  useLoadScript({ inBody: false, child: recaptchaScript });
  useLoadScript({
    inBody: false,
    child: headerScript,
    excludeUrls: ['/admin'],
  });
  useLoadScript({ inBody: true, child: bodyScript, excludeUrls: ['/admin'] });
  useLoadScript({ inBody: false, child: checklistScript });

  const { raiseAnalyticEventsRequest } = useContext(MarketingInfoContext);

  useEffect(() => {
    const useHandleKavaEvent = (event: MouseEvent) => {
      const eventName =
        (event.target as HTMLElement).getAttribute('datatype') || '';

      if (eventName) {
        raiseAnalyticEventsRequest(eventName);
      }
    };

    document.addEventListener('click', useHandleKavaEvent);
    return () => document.removeEventListener('click', useHandleKavaEvent);
  }, [pathname]);

  return (
    <Routes>
      <Route
        path="/*"
        element={<Navigate to={routesPaths.MANAGEMENT_OVERVIEW} />}
      />
      <Route path={routesPaths.GET_STARTED} element={<GetStarted />} />
      <Route
        path={routesPaths.GET_STARTED_2}
        element={<GetStarted isSecondary />}
      />
      <Route
        path={routesPaths.CREATE_ACCOUNT_EXTENDED}
        element={<CreateAccountExtended />}
      />
      <Route
        path={routesPaths.CREATE_ACCOUNT_BASE}
        element={<CreateAccountBase />}
      />
      <Route path={routesPaths.BUILD_PLAN} element={<BuildYourPlan />} />
      <Route path={routesPaths.VERIFY} element={<Verify />} />
      <Route path={routesPaths.ONE_LAST_STEP} element={<OneLastStep />} />
      <Route
        path={routesPaths.ONE_LAST_STEP_2}
        element={<OneLastStep isSecondary />}
      />
      <Route path={routesPaths.CHECK_YOUR_INBOX} element={<CheckYourInbox />} />
      <Route path={routesPaths.LINK_USED} element={<LinkUsed />} />
      <Route path={routesPaths.LOGIN} element={<Login />} />
      <Route path={routesPaths.FORGOT_PASSWORD} element={<ForgotPassword />} />
      <Route path={routesPaths.SET_PASSWORD} element={<SetPassword />} />
      <Route path={routesPaths.ABANDONED_CHECKOUT} element={<Checkout />} />
      <Route path={routesPaths.PLEASE_WAIT} element={<PleaseWait />} />
      <Route path={routesPaths.CONFIRMED} element={<Confirmed />} />
      <Route path={routesPaths.CHECKOUT} element={<Checkout />} />
      <Route path={routesPaths.MARKETING_INFO} element={<MarketingInfo />} />
      <Route
        path={routesPaths.WEBINAR_WIZARD}
        element={<WebinarCreatorWizard />}
      />
      <Route
        element={
          <AccountLayout>
            <Outlet />
          </AccountLayout>
        }
      >
        <Route
          path={routesPaths.MANAGEMENT_OVERVIEW}
          element={<PlansOverview />}
        />
        <Route
          path={routesPaths.MANAGEMENT_BILLING}
          element={<BillingDetails />}
        />
        <Route
          path={routesPaths.MANAGEMENT_INVOICE}
          element={<InvoiceHistory />}
        />
      </Route>
      <Route
        element={
          <AdminLayout>
            <Outlet />
          </AdminLayout>
        }
      >
        <Route
          path={routesPaths.ADMIN}
          element={<Navigate to={routesPaths.ADMIN_OVERVIEW} />}
        />
        <Route path={routesPaths.ADMIN_OVERVIEW} element={<h1>Overview</h1>} />
        <Route path={routesPaths.ADMIN_PRODUCTS} element={<AdminProducts />} />
        <Route
          path={routesPaths.ADMIN_SUBSCRIPTIONS}
          element={<AdminSubscriptions />}
        />
        <Route path={routesPaths.ADMIN_PARTNERS} element={<AdminPartners />} />
        <Route
          path={routesPaths.ADMIN_EMAIL_VALIDATIONS}
          element={<h1>Email validations</h1>}
        />
        <Route
          path={routesPaths.ADMIN_ENTERPRISE}
          element={<AdminEnterprise />}
        />
        <Route
          path={routesPaths.ADMIN_CHECKLISTS}
          element={<AdminChecklists />}
        />
        <Route
          path={routesPaths.ADMIN_ANNOUNCEMENTS}
          element={<AdminAnnouncements />}
        />
        <Route
          path={routesPaths.ADMIN_PRODUCT_UPDATE.replace(
            ':type',
            productType.PACKAGES
          )}
          element={<AdminPackageUpdate />}
        />
        <Route
          path={routesPaths.ADMIN_PRODUCT_UPDATE}
          element={<AdminPlanUpdate />}
        />
      </Route>
    </Routes>
  );
}

export default App;
